<script setup lang="ts">
/**
 * HealthView — `/health` route (Story 306).
 *
 * Periodically polls `provider.healthCheck()` and renders:
 * - Current ConnectionStatus with a Pajamas badge
 * - Last health-check result (ok / latency / message)
 * - Sparkline of latency over the last 60 polls
 * - List of the last 10 rate-limit events
 * - "Force health check" button
 * - Pause/resume toggle (challenger note: 1 poll/s burns rate-limit quota)
 *
 * The provider is injectable so unit tests can drive deterministic
 * results. The view subscribes to `events` from the provider for
 * `status:change`, `rate-limit`, `retry`, `error` and unmounts cleanly
 * (challenger gap: events bus leak).
 */
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import Card from '../components/Card.vue';
import Banner from '../components/Banner.vue';
import StatusBadge from '../components/StatusBadge.vue';
import Sparkline from '../components/Sparkline.vue';
import {
  HealthMonitor,
  DEFAULT_LATENCY_WINDOW,
  type LatencySample,
  type RateLimitEvent,
} from '../services/healthMonitor';

export interface HealthProvider {
  healthCheck(): Promise<{ ok: boolean; latencyMs: number; message?: string }>;
  getStatus?(): string;
  on?(
    event: 'status:change' | 'rate-limit' | 'retry' | 'error',
    handler: (payload: unknown) => void,
  ): void;
  off?(
    event: 'status:change' | 'rate-limit' | 'retry' | 'error',
    handler: (payload: unknown) => void,
  ): void;
}

const props = defineProps<{
  provider: HealthProvider;
  pollIntervalMs?: number;
  autoStart?: boolean;
}>();

const monitor = new HealthMonitor(DEFAULT_LATENCY_WINDOW, 10);
const samples = ref<ReadonlyArray<LatencySample>>([]);
const rateLimits = ref<ReadonlyArray<RateLimitEvent>>([]);
const status = ref<string>('disconnected');
const lastResult = ref<{ ok: boolean; latencyMs: number; message?: string } | null>(null);
const isPolling = ref<boolean>(props.autoStart !== false);
const isChecking = ref<boolean>(false);
const errorMessage = ref<string | null>(null);

const pollIntervalMs = computed(() => props.pollIntervalMs ?? 1000);
let pollHandle: ReturnType<typeof setInterval> | null = null;

const statusHandler = (payload: unknown): void => {
  if (typeof payload === 'string') status.value = payload;
  else if (
    payload && typeof payload === 'object' && 'status' in payload &&
    typeof (payload as { status: unknown }).status === 'string'
  ) {
    status.value = (payload as { status: string }).status;
  }
};

const rateLimitHandler = (payload: unknown): void => {
  let retryAfterMs = 0;
  if (
    payload && typeof payload === 'object' && 'retryAfterMs' in payload &&
    typeof (payload as { retryAfterMs: unknown }).retryAfterMs === 'number'
  ) {
    retryAfterMs = (payload as { retryAfterMs: number }).retryAfterMs;
  }
  monitor.recordRateLimit({ timestamp: Date.now(), retryAfterMs });
  rateLimits.value = monitor.getRateLimits();
};

const errorHandler = (payload: unknown): void => {
  if (
    payload && typeof payload === 'object' && 'message' in payload &&
    typeof (payload as { message: unknown }).message === 'string'
  ) {
    errorMessage.value = (payload as { message: string }).message;
  }
};

async function check(): Promise<void> {
  if (isChecking.value) return;
  isChecking.value = true;
  errorMessage.value = null;
  const usesManualStatus =
    typeof props.provider.getStatus !== 'function' && typeof props.provider.on !== 'function';
  try {
    const result = await props.provider.healthCheck();
    lastResult.value = result;
    if (usesManualStatus) {
      status.value = result.ok ? 'connected' : 'error';
    }
    monitor.recordLatency({
      timestamp: Date.now(),
      latencyMs: Math.max(0, Math.round(result.latencyMs)),
      ok: result.ok,
    });
    samples.value = monitor.getSamples();
  } catch (err) {
    const message = err instanceof Error && err.message ? err.message : 'Health check failed.';
    errorMessage.value = message;
    if (usesManualStatus) {
      status.value = 'error';
    }
    monitor.recordLatency({ timestamp: Date.now(), latencyMs: 0, ok: false });
    samples.value = monitor.getSamples();
  } finally {
    isChecking.value = false;
  }
}

function togglePolling(): void {
  isPolling.value = !isPolling.value;
  if (isPolling.value) {
    startPolling();
  } else {
    stopPolling();
  }
}

function startPolling(): void {
  stopPolling();
  pollHandle = setInterval(() => {
    void check();
  }, pollIntervalMs.value);
}

function stopPolling(): void {
  if (pollHandle !== null) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
}

onMounted(() => {
  if (typeof props.provider.getStatus === 'function') {
    status.value = props.provider.getStatus();
  }
  if (typeof props.provider.on === 'function') {
    props.provider.on('status:change', statusHandler);
    props.provider.on('rate-limit', rateLimitHandler);
    props.provider.on('error', errorHandler);
  }
  void check();
  if (isPolling.value) startPolling();
});

onBeforeUnmount(() => {
  stopPolling();
  if (typeof props.provider.off === 'function') {
    props.provider.off('status:change', statusHandler);
    props.provider.off('rate-limit', rateLimitHandler);
    props.provider.off('error', errorHandler);
  }
});

const latencyValues = computed(() => samples.value.map((s) => s.latencyMs));
const okMask = computed(() => samples.value.map((s) => s.ok));
const lastEventLabel = (e: RateLimitEvent): string => {
  const seconds = Math.round((Date.now() - e.timestamp) / 1000);
  return seconds <= 0 ? 'just now' : `${seconds}s ago`;
};
</script>

<template>
  <div>
    <PageHeader
      title="Connection health"
      subtitle="Live status, latency sparkline, and recent rate-limit events from ClaudeProvider."
    >
      <template #actions>
        <button
          type="button"
          class="cwc-health__force"
          data-testid="force-button"
          :disabled="isChecking"
          @click="check"
        >
          <span v-if="isChecking">Checking...</span>
          <span v-else>Force health check</span>
        </button>
        <button
          type="button"
          class="cwc-health__pause"
          data-testid="pause-button"
          @click="togglePolling"
        >
          {{ isPolling ? 'Pause polling' : 'Resume polling' }}
        </button>
      </template>
    </PageHeader>

    <Banner v-if="errorMessage" variant="danger" title="Health error">{{ errorMessage }}</Banner>

    <div class="cwc-health__grid">
      <Card title="Status">
        <p>
          Current status:
          <StatusBadge :status="status" data-testid="status-badge" />
        </p>
        <p v-if="lastResult" data-testid="last-result">
          Last poll:
          <strong>{{ lastResult.ok ? 'ok' : 'failing' }}</strong>
          ({{ lastResult.latencyMs }} ms){{ lastResult.message ? ' — ' + lastResult.message : '' }}
        </p>
      </Card>

      <Card title="Latency (last 60 polls)">
        <Sparkline :values="latencyValues" :ok-mask="okMask" />
      </Card>

      <Card title="Recent rate-limit events">
        <ul v-if="rateLimits.length > 0" class="cwc-health__rate-limits" data-testid="rate-limit-list">
          <li v-for="(event, idx) in rateLimits" :key="`${event.timestamp}-${idx}`">
            retry after {{ event.retryAfterMs }} ms — {{ lastEventLabel(event) }}
          </li>
        </ul>
        <p v-else class="cwc-health__empty">No rate-limit events yet.</p>
      </Card>
    </div>
  </div>
</template>

<style scoped>
.cwc-health__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--gl-spacing-5);
  margin-top: var(--gl-spacing-4);
}

.cwc-health__force,
.cwc-health__pause {
  appearance: none;
  background: var(--gl-bg-surface);
  color: var(--gl-fg-default);
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-4);
  cursor: pointer;
  font: inherit;
}

.cwc-health__force:disabled { opacity: 0.6; cursor: not-allowed; }

.cwc-health__rate-limits {
  margin: 0;
  padding-left: var(--gl-spacing-5);
}

.cwc-health__empty {
  color: var(--gl-fg-subtle);
  font-style: italic;
  margin: 0;
}
</style>
